import React, { ReactNode } from 'react';
import { FloralCornerDecoration, WatercolorBackground } from './FloralDecorations';

interface FloralCardProps {
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'elevated' | 'minimal';
  showCornerDecorations?: boolean;
  showWatercolorBackground?: boolean;
  padding?: 'sm' | 'md' | 'lg' | 'xl';
  'aria-label'?: string;
  role?: string;
}

const variantClasses = {
  default: 'bg-white/90 backdrop-blur-sm border border-sage-200 shadow-md',
  elevated: 'bg-white/95 backdrop-blur-md border border-sage-200 shadow-lg',
  minimal: 'bg-white/80 backdrop-blur-sm border border-sage-100 shadow-sm',
};

const paddingClasses = {
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
  xl: 'p-10',
};

export const FloralCard: React.FC<FloralCardProps> = ({
  children,
  className = '',
  variant = 'default',
  showCornerDecorations = true,
  showWatercolorBackground = true,
  padding = 'lg',
  'aria-label': ariaLabel,
  role,
}) => {
  return (
    <div
      className={`relative rounded-xl overflow-hidden ${variantClasses[variant]} ${paddingClasses[padding]} ${className}`}
      aria-label={ariaLabel}
      role={role}
    >
      {showWatercolorBackground && (
        <WatercolorBackground className="opacity-50" />
      )}
      
      {showCornerDecorations && (
        <>
          <FloralCornerDecoration position="top-left" size="sm" />
          <FloralCornerDecoration position="top-right" size="sm" />
          <FloralCornerDecoration position="bottom-left" size="sm" />
          <FloralCornerDecoration position="bottom-right" size="sm" />
        </>
      )}
      
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

export class FloralErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Handle error silently
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <FloralCard variant="minimal" className="text-center">
            <div className="text-sage-600">
              <h3 className="text-lg font-semibold mb-2">Something went wrong</h3>
              <p className="text-sm">We're sorry, but there was an error loading this section.</p>
              <button
                onClick={() => this.setState({ hasError: false })}
                className="mt-4 px-4 py-2 bg-sage-600 text-white rounded-md hover:bg-sage-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </FloralCard>
        )
      );
    }

    return this.props.children;
  }
}

// Loading Component
export const FloralLoading: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => {
  return (
    <FloralCard className="text-center" showCornerDecorations={false}>
      <div className="flex flex-col items-center space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-600"></div>
        <p className="text-sage-600 text-sm">{message}</p>
      </div>
    </FloralCard>
  );
};